import { component } from 'vue-tsx-support'

import { ShoppingListItem } from '@/datastore/models'

import ConfirmModal from '@/components/ConfirmModal'
import HeaderBar from '@/components/HeaderBar'

import grocery from '@/store/modules/grocery'

import ShoppingListCard from './components/ShoppingListCard'

interface Data {
	itemToRemove: ShoppingListItem | null
	sortAscending: boolean
	sortBy: 'name' | 'date'
}

export default component({
	name: 'ShoppingListPage',
	data(): Data {
		return {
			itemToRemove: null,
			sortAscending: true,
			sortBy: 'name',
		}
	},
	computed: {
		isSortingByDate(): boolean {
			return this.sortBy === 'date'
		},
		isSortingByName(): boolean {
			return this.sortBy === 'name'
		},
		showConfirmRemoveModal(): boolean {
			return !!this.itemToRemove
		},
		sortedShoppingListItems(): ShoppingListItem[] {
			const sorted = [...grocery.shoppingListItems]
				.sort(this.sortBy === 'date' ? dateSorter : nameSorter)

			if (!this.sortAscending) {
				sorted.reverse()
			}

			return sorted
		},
	},
	methods: {
		addToCart(item: ShoppingListItem): void {
			grocery.deleteShoppingListItem(item.$id)
		},
		cancelRemoveItem(): void {
			this.itemToRemove = null
		},
		confirmRemoveItem(item: ShoppingListItem): void {
			this.itemToRemove = item
		},
		removeItem(): void {
			if (this.itemToRemove) {
				grocery.deleteShoppingListItem(this.itemToRemove.$id)
				this.itemToRemove = null
			}
		},
	},
	render() {
		const sortByDate = () => {
			if (this.isSortingByDate) {
				this.sortAscending = !this.sortAscending
			}
			else {
				this.sortBy = 'date'
			}
		}

		const sortByName = () => {
			if (this.isSortingByName) {
				this.sortAscending = !this.sortAscending
			}
			else {
				this.sortBy = 'name'
			}
		}

		return (
			<div>
				<HeaderBar title="Shopping List">
					<div class="right menu">
						<div class={['icon link item', { active: this.isSortingByName }]} onClick={sortByName}>
							<i class={['large sort alphabet icon', this.sortAscending ? 'down' : 'up']} />
						</div>
						<div class={['icon link item', { active: this.isSortingByDate }]} onClick={sortByDate}>
							<i class={['large sort numeric icon', this.sortAscending ? 'down' : 'up']} />
						</div>
					</div>
				</HeaderBar>
				{this.sortedShoppingListItems.length > 0 ?
					<div class="ui three stackable cards container">
						{this.sortedShoppingListItems.map(item =>
							<ShoppingListCard
								item={item}
								onAddToCart={() => this.addToCart(item)}
								onRemove={() => this.confirmRemoveItem(item)}
								/>)
						}
					</div>
					:
					<div class="ui placeholder segment">
						<div class="ui icon header">
							<i class="check icon" />
							You don't have anything on your shopping list.
						</div>
						<router-link class="ui primary button" tag="div" to="/pantry/all">
							Add Pantry Items
						</router-link>
					</div>
				}
				{this.itemToRemove &&
					<ConfirmModal
						confirmClass="negative"
						confirmText={`Yes, Remove ${this.itemToRemove.name}`}
						header="Remove from Shopping List?"
						onConfirm={this.removeItem}
						onDecline={this.cancelRemoveItem}
						open={this.showConfirmRemoveModal}
						>
						Are you sure you want to remove <b>{this.itemToRemove.name}</b> from the shopping list?
					</ConfirmModal>
				}
			</div>
		)
	},
})

function dateSorter(a: ShoppingListItem, b: ShoppingListItem): number {
	return (a.createdAt ?? 0) - (b.createdAt ?? 0)
}

function nameSorter(a: ShoppingListItem, b: ShoppingListItem): number {
	return a.name.localeCompare(b.name)
}
